import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { ResponsiveImg } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { isTypes } from '#src/lib/gql/index.ts';

import type {
  Avatar_anonymousUser,
  Avatar_ethereumAccount,
  Avatar_publicUserInfoInterface,
  Avatar_starkwareAccount,
  Avatar_user,
  Avatar_userSportProfileInterface,
} from './__generated__/index.graphql.ts';
import placeholders from './assets/placeholders.svg';

type BaseProps = {
  rounded?: boolean;
  size: number;
};

type DisabledAvatarDisplayProps = BaseProps & {
  invert?: boolean;
};

type PlaceHolderAvatarProps = BaseProps & {
  user?: { nickname?: string };
};

type PictureAvatarProps = BaseProps & {
  invert?: boolean;
  user: { nickname: string };
  pictureUrl: NonNullable<Avatar_publicUserInfoInterface['pictureUrl']>;
};

type AvatarDisplayProps = Omit<BaseProps, 'style'> & {
  invert?: boolean;
  largePictureUrl?: string | null;
  hideChampionshipFrame?: boolean;
  framePictureUrl?: string;
};

type Props = {
  user:
    | Avatar_publicUserInfoInterface
    | Avatar_anonymousUser
    | Avatar_ethereumAccount
    | Avatar_starkwareAccount
    | Avatar_user;
  placeholderUrl?: string;
} & AvatarDisplayProps;

const isAnonymous = (
  user:
    | Avatar_publicUserInfoInterface
    | Avatar_anonymousUser
    | Avatar_ethereumAccount
    | Avatar_starkwareAccount
    | Avatar_user
): user is
  | Avatar_anonymousUser
  | Avatar_ethereumAccount
  | Avatar_starkwareAccount => {
  return isTypes(user, [
    'AnonymousUser',
    'EthereumAccount',
    'StarkwareAccount',
  ]);
};

const Root = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  object-fit: cover;
  background-color: var(--c-nd-800);
  border-radius: var(--unit);
  aspect-ratio: 1;
  color: var(--c-onBase-light);
  font: var(--t-16);
  text-transform: uppercase;
  &.isRounded {
    border-radius: 50%;
  }
  &.inverted {
    background-color: var(--c-surface-secondary);
    color: var(--c-onBase-secondary);
  }
`;
const RelativeWrapper = styled.div`
  position: relative;
`;
const PictureFrameUrl = styled.div`
  position: absolute;
  inset: calc(-1 * var(--unit));
  background: no-repeat center / cover var(--bg);
  pointer-events: none;
`;
const PictureFrameUrlDescriptionWrapper = styled.div`
  padding: var(--unit);
`;

const DisabledAvatar = ({
  rounded,
  invert,
  size,
}: DisabledAvatarDisplayProps) => {
  return (
    <Root
      className={classnames({ isRounded: rounded, inverted: invert })}
      style={{
        width: `calc(${size} * var(--unit))`,
        height: `calc(${size} * var(--unit))`,
      }}
    >
      ?
    </Root>
  );
};

const getLetterIndex = (letter?: string) =>
  Math.abs(((letter?.toLowerCase()?.charCodeAt(0) ?? 0) - 97) % 25) * 100;
const Placeholder = styled.div`
  background: url(${placeholders});
  /* Use 100% height instead of cover to work around chrome zoom issue
   * https://issues.chromium.org/issues/40325175 */
  background-size: auto 100%;
  width: 100%;
  height: 100%;
  border-radius: inherit;
`;

export const PlaceHolderAvatar = ({
  rounded,
  user,
  size,
}: PlaceHolderAvatarProps) => {
  return (
    <Root
      className={classnames({ isRounded: rounded })}
      style={{
        width: `calc(${size} * var(--unit))`,
        height: `calc(${size} * var(--unit))`,
      }}
    >
      <Placeholder
        role="img"
        aria-label={user?.nickname}
        style={{
          backgroundPosition: `${getLetterIndex(user?.nickname?.[0])}% 0`,
        }}
      />
    </Root>
  );
};

export const PictureAvatar = ({
  rounded,
  invert,
  user,
  pictureUrl,
  size,
}: PictureAvatarProps) => {
  return (
    <Root
      as={ResponsiveImg}
      src={pictureUrl}
      className={classnames({ isRounded: rounded, inverted: invert })}
      cropWidth={80}
      alt={user.nickname!}
      style={{
        width: `calc(${size} * var(--unit))`,
        height: `calc(${size} * var(--unit))`,
      }}
    />
  );
};

export const Avatar = ({
  user,
  placeholderUrl,
  largePictureUrl,
  size,
  hideChampionshipFrame,
  rounded: roundedProp,
  framePictureUrl,
  ...rest
}: Props) => {
  const { sport } = useSportContext();

  const disabledAvatar = isAnonymous(user) || user.suspended;

  const sportUserProfile = disabledAvatar
    ? null
    : {
        [Sport.FOOTBALL]: user.footballUserProfile,
        [Sport.NBA]: user.nbaUserProfile,
        [Sport.BASEBALL]: user.baseballUserProfile,
      }[sport];

  const displayFootballProfilePictureFrame =
    !!sportUserProfile?.pictureFrame &&
    !hideChampionshipFrame &&
    size &&
    size > 2;

  const rounded = displayFootballProfilePictureFrame || roundedProp;

  if (disabledAvatar) {
    return <DisabledAvatar rounded={rounded} {...rest} size={size} />;
  }

  const { pictureUrl } = user || {};
  const url = largePictureUrl || pictureUrl;

  let avatar: ReactNode;
  if (url) {
    avatar = (
      <PictureAvatar
        user={user}
        pictureUrl={url}
        rounded={rounded}
        size={size}
        {...rest}
      />
    );
  } else if (placeholderUrl) {
    avatar = (
      <PictureAvatar
        user={user}
        pictureUrl={placeholderUrl}
        rounded={rounded}
        size={size}
        {...rest}
      />
    );
  } else {
    avatar = (
      <PlaceHolderAvatar user={user} rounded={rounded} size={size} {...rest} />
    );
  }

  if (displayFootballProfilePictureFrame) {
    return (
      <Dropdown
        triggerOnHover
        label={({ onMouseEnter, onMouseLeave }) => (
          <RelativeWrapper
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
              width: `calc(${size} * var(--unit))`,
              height: `calc(${size} * var(--unit))`,
            }}
          >
            <PictureFrameUrl
              style={
                {
                  '--bg': `url(${framePictureUrl || sportUserProfile.pictureFrame?.pictureUrl})`,
                } as CSSProperties
              }
            />
            {avatar}
          </RelativeWrapper>
        )}
      >
        <PictureFrameUrlDescriptionWrapper>
          <LabelM>{sportUserProfile.pictureFrame?.name}</LabelM>
        </PictureFrameUrlDescriptionWrapper>
      </Dropdown>
    );
  }

  return avatar;
};

const UserSportProfileInterfaceFragment = gql`
  fragment Avatar_userSportProfileInterface on UserSportProfileInterface {
    id
    pictureFrame {
      id
      name
      pictureUrl
    }
  }
` as TypedDocumentNode<Avatar_userSportProfileInterface>;

Avatar.fragments = {
  user: gql`
    fragment Avatar_user on User {
      slug
      nickname
      suspended
      pictureUrl(derivative: "avatar")
      footballUserProfile {
        id
        ...Avatar_userSportProfileInterface
      }
      nbaUserProfile {
        id
        ...Avatar_userSportProfileInterface
      }
      baseballUserProfile {
        id
        ...Avatar_userSportProfileInterface
      }
    }
    ${UserSportProfileInterfaceFragment}
  ` as TypedDocumentNode<Avatar_user>,
  publicUserInfoInterface: gql`
    fragment Avatar_publicUserInfoInterface on PublicUserInfoInterface {
      slug
      nickname
      suspended
      pictureUrl(derivative: "avatar")
      footballUserProfile {
        id
        ...Avatar_userSportProfileInterface
      }
      nbaUserProfile {
        id
        ...Avatar_userSportProfileInterface
      }
      baseballUserProfile {
        id
        ...Avatar_userSportProfileInterface
      }
    }
    ${UserSportProfileInterfaceFragment}
  ` as TypedDocumentNode<Avatar_publicUserInfoInterface>,
  anonymousUser: gql`
    fragment Avatar_anonymousUser on AnonymousUser {
      id
    }
  ` as TypedDocumentNode<Avatar_anonymousUser>,
  ethereumAccount: gql`
    fragment Avatar_ethereumAccount on EthereumAccount {
      id
    }
  ` as TypedDocumentNode<Avatar_ethereumAccount>,
  starkwareAccount: gql`
    fragment Avatar_starkwareAccount on StarkwareAccount {
      id
    }
  ` as TypedDocumentNode<Avatar_starkwareAccount>,
};
